import React, { useState } from "react";
import {
  Box,
  Stack,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  Favorite,
  FavoriteBorder,
  ShareOutlined,
  ContentCopy,
} from "@mui/icons-material";
import ProductImage from "../Utils/ProductImage";

export default function ProductGallery({
  product,
  isFavorite,
  favoriteLoading,
  handleToggleFavorite,
}) {
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const getShareUrl = () => {
    const currentUrl = new URL(window.location.href);

    const developmentHosts = new Set([
      "localhost",
      "127.0.0.1",
      "192.168.0.105",
      "192.168.1.105",
    ]);

    if (developmentHosts.has(currentUrl.hostname)) {
      return `https://zomioo.ir${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    }

    return currentUrl.href;
  };

  const shareUrl = getShareUrl();

  const handleShare = async () => {
    const shareData = {
      title: product?.title || "محصول زومیو",
      url: shareUrl,
    };

    if (typeof navigator.share === "function") {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }

        console.error("Native sharing failed:", error);
      }
    }

    setShareOpen(true);
  };

  const handleCopyLink = async (event) => {
    event?.stopPropagation();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.readOnly = true;

        Object.assign(textArea.style, {
          position: "fixed",
          top: "0",
          left: "0",
          width: "1px",
          height: "1px",
          padding: "0",
          border: "0",
          opacity: "0",
          pointerEvents: "none",
        });

        document.body.appendChild(textArea);

        try {
          textArea.focus();
          textArea.select();
          textArea.setSelectionRange(0, textArea.value.length);

          if (!document.execCommand("copy")) {
            throw new Error("Browser could not copy the URL");
          }
        } finally {
          textArea.remove();
        }
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy failed:", error);
      setCopied(false);
    }
  };

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      {/* Main Image */}
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "grey.100",
          overflow: "hidden",
        }}
      >
        <ProductImage images={product?.images} title={product?.title} />
      </Box>

      {/* Actions outside the image */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
        spacing={1}
        sx={{
          mt: 1,
        }}
      >
        <IconButton
          onClick={handleToggleFavorite}
          disabled={favoriteLoading}
          aria-label="favorite"
          size="medium"
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          {favoriteLoading ? (
            <CircularProgress size={20} sx={{ color: "#ef4056" }} />
          ) : isFavorite ? (
            <Favorite sx={{ color: "#ef4056" }} />
          ) : (
            <FavoriteBorder sx={{ color: "grey.600" }} />
          )}
        </IconButton>

        <IconButton
          onClick={handleShare}
          aria-label="share"
          size="medium"
          sx={{
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <ShareOutlined sx={{ color: "grey.700", fontSize: "1.25rem" }} />
        </IconButton>
      </Stack>
      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        maxWidth="xs"
        fullWidth
        dir="rtl"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle fontWeight={800} fontSize="1.1rem">
          اشتراک‌گذاری محصول
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            لینک زیر را کپی کرده و با دیگران به اشتراک بگذارید:
          </Typography>

          <TextField
            fullWidth
            value={shareUrl}
            size="small"
            inputProps={{
              readOnly: true,
              onClick: (e) => e.target.select(),
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopyLink}
                      aria-label="copy product link"
                      edge="end"
                      size="small"
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                direction: "ltr",
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShareOpen(false)}
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            بستن
          </Button>

          <Button
            onClick={handleCopyLink}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: "#8257ac",
              fontWeight: 700,
              borderRadius: 2,
              "&:hover": { bgcolor: "#6f449a" },
            }}
          >
            {copied ? "کپی شد!" : "کپی لینک"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
